import React, { useEffect, useState } from "react";
import {
  getYear,
  addDays,
  addBusinessDays,
  differenceInCalendarDays,
  differenceInBusinessDays,
} from "date-fns";

import { Box, Stack } from "@mui/material";

import usePayroll from "../../hooks/usePayroll";

import EditorToolbar from "./EditorToolbar";
import Input from "../elements/Input";
import DateField from "../elements/Date";
import Switch from "../elements/Switch";
import Select from "../elements/Select";
import Loading from "../ui/Loading";

const PayPeriodEditor = ({ payPeriodId }) => {
  const { payPeriods, getPayPeriod, updatePayPeriod } = usePayroll();

  const [data, setData] = useState(null);

  useEffect(() => {
    if (!Array.isArray(payPeriods)) return;

    const results = getPayPeriod(payPeriodId);

    if (results) {
      setData(results);
    } else {
      setData({
        id: null,
        fiscalYear: getYear(new Date()),
        name: "",
        startDate: "",
        endDate: "",
        processDate: "",
        status: "future",
        mileageRate: 0,
      });
    }
  }, [payPeriods]);

  const handleFieldUpdate = (field, newValue) => {
    setData((current) => {
      let newData = { ...current };
      newData = { ...current, [field]: newValue };

      if (field === "startDate") {
        let endDiff = 13;
        let processDiff = 5;

        if (newData.endDate) {
          endDiff = differenceInCalendarDays(
            current.endDate,
            current.startDate,
          );

          processDiff = differenceInBusinessDays(
            current.processDate,
            current.endDate,
          );
        }

        newData.endDate = addDays(newData.startDate, endDiff);

        newData.processDate = addBusinessDays(newData.endDate, processDiff);
      }

      return newData;
    });
  };

  const handleSave = async () => {
    await updatePayPeriod(data);
  };

  if (!data) return <Loading />;

  return (
    <Box>
      <EditorToolbar onSave={handleSave} />
      <Box sx={{ padding: "6px" }}>
        <Stack spacing={1}>
          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box sx={{ flex: 1 }}>
              <Input
                label="Year"
                value={data.fiscalYear}
                onChange={(newValue) =>
                  handleFieldUpdate("fiscalYear", newValue)
                }
                type="number"
                fullwidth={true}
              />
            </Box>
            <Box sx={{ flex: 4 }}>
              <Input
                label="Name"
                value={data.name}
                onChange={(newValue) => handleFieldUpdate("name", newValue)}
                fullwidth={true}
              />
            </Box>
          </Box>

          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box></Box>
            <DateField
              label="Period Start"
              value={data.startDate}
              onChange={(newValue) => handleFieldUpdate("startDate", newValue)}
              fullwidth={true}
            />
            <DateField
              label="Period End"
              value={data.endDate}
              onChange={(newValue) => handleFieldUpdate("endDate", newValue)}
              fullwidth={true}
            />
            <DateField
              label="Process Date"
              value={data.processDate}
              onChange={(newValue) =>
                handleFieldUpdate("processDate", newValue)
              }
              fullwidth={true}
            />
          </Box>

          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box sx={{ flex: 1 }}>
              <Input
                label="Distance Rate ($/km)"
                value={data.mileageRate}
                onChange={(newValue) =>
                  handleFieldUpdate("mileageRate", parseFloat(newValue) || 0)
                }
                type="number"
                fullwidth={true}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Select
                label="Status"
                items={[
                  { id: "future", name: "Future" },
                  { id: "open", name: "Open" },
                  { id: "closed", name: "Closed" },
                ]}
                value={data.status}
                onChange={(newValue) => handleFieldUpdate("status", newValue)}
                fullwidth={true}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PayPeriodEditor;
